import { useAuth0 } from "@auth0/auth0-react";
import { Check, Loader2 } from "lucide-react";
import { useCallback } from "react";

import { ExtensionMessageType } from "@punto/frontend-shared/extension";
import { clearStoredTableState } from "@punto/frontend-shared/hooks/use-table-state";
import { useTranslation } from "@punto/frontend-shared/i18n/i18next";
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@punto/ui/components/dropdown-menu";

import { sendBridgeMessage } from "@/lib/extension/bridge-client";
import { useUserOrganizations } from "@/modules/auth/hooks/use-user-organizations";

export const OrganizationSwitcher = () => {
  const { t } = useTranslation();
  const { loginWithRedirect } = useAuth0();
  const { currentOrganization, availableOrganizations, isLoading } =
    useUserOrganizations();

  const handleOrganizationSelect = useCallback(
    async (organizationId: string) => {
      if (organizationId === currentOrganization?.referenceId) return;

      // Filters belong to the organization they were built in, as in Portus's
      // selector. Restored in another one, a «Clientes de Punto» picked in
      // Gestalde comes back as a chip the Punto org does not offer, and a stored
      // customer id as a filter nothing there matches (P-3375).
      clearStoredTableState();

      // Sync org to extension before redirecting so it can re-authenticate
      sendBridgeMessage(ExtensionMessageType.SYNC_ORGANIZATION, {
        organizationId,
      });
      await loginWithRedirect({
        appState: {
          returnTo: "/",
        },
        authorizationParams: {
          organization: organizationId,
        },
      });
    },
    [loginWithRedirect, currentOrganization?.referenceId],
  );

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <span>{t("OrganizationSwitcher.organizations")}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-[200px]">
        {isLoading ? (
          <DropdownMenuItem disabled className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{t("OrganizationSwitcher.loading")}</span>
          </DropdownMenuItem>
        ) : (
          availableOrganizations.map((org) => (
            <DropdownMenuItem
              key={org.referenceId}
              onClick={() => void handleOrganizationSelect(org.referenceId)}
              className="flex items-center justify-between"
            >
              <span>{org.name}</span>
              {currentOrganization?.referenceId === org.referenceId && (
                <Check className="ml-2 h-4 w-4" />
              )}
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
};
